import { useState } from "react";
import { Link, useLoaderData, useNavigate, useParams, useRevalidator, type LoaderFunctionArgs } from "react-router";
import { Lines, Loading, SettingsRowsSkeleton } from "../../components/skeletons.jsx";
import { Deferred, ErrorState } from "../../components/states.jsx";
import { useI18n } from "../../i18n/index.js";
import type { ConnectorLink, ConnectorResources, ConnectorSummary, SourceSync, WorkspaceApi } from "../../lib/api.js";
import { Avatar, Badge, Button, Callout, ChevronLeftIcon, ConfirmDialog, LinkButton, Markdown, PageHeader, SettingsBlock, SettingsRow, SettingsSection, buttonClass, useToast, Skeleton } from "../../ui/index.js";
import { useSyncError } from "../workspace-setup.jsx";
import { ConnectorLogo, connectorStatus, connectorsLoader, localized, useInstallOutcome } from "./connectors.jsx";

/** Settings: one platform — its installation, what it covers, and who is linked (D-0018, D-0019). */

export function connectorLoader(args: LoaderFunctionArgs) {
  return { ...connectorsLoader(args), provider: args.params.provider ?? "" };
}

// Resource kinds with a noun of their own; anything else reads as "items".
const RESOURCE_KINDS = new Set(["repository", "page"]);
const SHOWN_RESOURCES = 6;

function LinkRow({ link, provider, workspace, canManage, onChanged }: { link: ConnectorLink; provider: ConnectorSummary; workspace: WorkspaceApi; canManage: boolean; onChanged: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  return (
    <SettingsRow
      label={
        <span className="dt-inline dt-gap-2 dt-align-center">
          <Avatar name={link.member?.name ?? link.externalName} src={link.member?.image} round />
          {link.member?.name ?? t("connectors.links.left")}
        </span>
      }
      description={`${provider.name} · ${link.externalName}`}
    >
      <span className="dt-inline dt-gap-2 dt-align-center">
        {link.member ? <Badge tone="neutral">{t(`connectors.links.method.${link.method}`)}</Badge> : <Badge tone="warning">{t("connectors.links.inactive")}</Badge>}
        {canManage && (
          <ConfirmDialog
            trigger={
              <Button variant="ghost" size="sm">
                {t("connectors.links.unlink")}
              </Button>
            }
            title={t("connectors.links.unlink.title", { name: link.externalName })}
            confirmLabel={t("connectors.links.unlink")}
            destructive
            onConfirm={async () => {
              await workspace.unlinkConnectorUser(provider.provider, link.externalUserId);
              toast.neutral(t("connectors.links.unlinked"), link.externalName);
              onChanged();
            }}
          />
        )}
      </span>
    </SettingsRow>
  );
}

/** What the installation covers. "All" is a statement, not an empty list: it includes whatever is added later. */
function Resources({ resources }: { resources: ConnectorResources }) {
  const { t, tPlural } = useI18n();
  const noun = RESOURCE_KINDS.has(resources.kind) ? (resources.kind as "repository" | "page") : "generic";
  if (resources.selection === "all") return <p className="dt-settings-row__desc">{t(`connectors.resources.all.${noun}`)}</p>;
  const shown = resources.items.slice(0, SHOWN_RESOURCES);
  // Pages are counted with everything under them but named only where someone picked them, so "more" counts names.
  const more = (resources.kind === "page" ? resources.items.length : Math.max(resources.total, resources.items.length)) - shown.length;
  return (
    <p className="dt-settings-row__desc" data-testid="connector-resources">
      {resources.truncated && noun === "page" ? t("connectors.resources.count.page.atLeast", { count: resources.total }) : tPlural(`connectors.resources.count.${noun}`, resources.total)}
      {shown.length > 0 && ": "}
      {shown.map((item, i) => (
        <span key={item}>
          {i > 0 && ", "}
          <code>{item}</code>
        </span>
      ))}
      {more > 0 && ` ${t("connectors.resources.more", { count: more })}`}
    </p>
  );
}

const SYNC_TONES = { queued: "neutral", reading: "info", processing: "info", succeeded: "success", failed: "danger" } as const;
const SKIP_REASONS = ["restricted", "overLimit", "unreadable"] as const;

/** Exploring the connector for decisions (D-0022, D-0023): how the last exploration went, and another one. */
function Sync({ connector, workspace, canManage }: { connector: ConnectorSummary; workspace: WorkspaceApi; canManage: boolean }) {
  const { t, tPlural, formatDateTime } = useI18n();
  const toast = useToast();
  const revalidator = useRevalidator();
  const syncError = useSyncError();
  const [busy, setBusy] = useState(false);
  const last: SourceSync | null | undefined = connector.lastSync;
  const active = last?.status === "queued" || last?.status === "reading" || last?.status === "processing";
  const covered = last?.coverage?.[connector.provider];
  const facts = last
    ? [
        formatDateTime(last.finishedAt ?? last.requestedAt),
        // How far the agent got in this connector; a read from before exploring counts pages instead.
        ...(covered ? [t("connectors.sync.coverage", { read: covered.read, listed: covered.listed })] : last.documents != null ? [tPlural("connectors.sync.documents", last.documents)] : []),
        ...(last.candidates != null ? [tPlural("connectors.sync.candidates", last.candidates)] : []),
        ...SKIP_REASONS.filter((r) => last.skipped[r]).map((r) => t(`connectors.sync.skipped.${r}`, { count: last.skipped[r]! })),
        ...(last.status === "failed" && last.error ? [syncError(last.error)] : []),
      ].join(" · ")
    : undefined;
  return (
    <SettingsSection title={t("connectors.sync.title")}>
      <SettingsRow label={last ? <Badge tone={SYNC_TONES[last.status]}>{t(`connectors.sync.status.${last.status}`)}</Badge> : t("connectors.sync.never")} description={facts} data-testid="connector-sync">
        {canManage && (
          <Button
            variant="secondary"
            size="sm"
            loading={busy}
            disabled={active}
            onClick={async () => {
              setBusy(true);
              try {
                await workspace.syncConnector(connector.provider);
                toast.success(t("connectors.sync.started", { name: connector.name }));
                await revalidator.revalidate();
              } catch (err) {
                toast.danger(t("connectors.sync.failed", { name: connector.name }), err instanceof Error ? err.message : String(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            {t("connectors.sync.now")}
          </Button>
        )}
      </SettingsRow>
    </SettingsSection>
  );
}

function Connector({ connector, workspace, listPath, canManage, viewerId }: { connector: ConnectorSummary; workspace: WorkspaceApi; listPath: string; canManage: boolean; viewerId: string }) {
  const { t, locale, formatDate } = useI18n();
  const toast = useToast();
  const revalidator = useRevalidator();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const { installation } = connector;
  const status = connectorStatus(connector);
  const detail = localized(connector.detail, locale);
  const mine = connector.links.find((l) => l.userId === viewerId);
  const returnTo = `${listPath}/${connector.provider}`;

  // Both leave the app for the provider and come back through the OAuth callback.
  const leaveFor = async (start: () => Promise<{ url: string }>, failed: "connectors.installFailed" | "connectors.identifyFailed") => {
    setBusy(true);
    try {
      window.location.assign((await start()).url);
    } catch (err) {
      setBusy(false);
      toast.danger(t(failed, { name: connector.name }), err instanceof Error ? err.message : String(err));
    }
  };
  const install = () => leaveFor(() => workspace.installConnector(connector.provider, returnTo), "connectors.installFailed");
  const identify = () => leaveFor(() => workspace.identifyConnectorAccount(connector.provider, returnTo), "connectors.identifyFailed");

  return (
    <div data-testid={`connector-${connector.provider}`}>
      <BackToList to={listPath} />
      <PageHeader
        title={
          <span className="dt-connector-title">
            <ConnectorLogo connector={connector} size={44} />
            {connector.name}
            {status && <Badge tone={status.tone}>{t(status.key)}</Badge>}
          </span>
        }
        description={localized(connector.description, locale)}
        actions={
          installation ? (
            canManage && (
              <ConfirmDialog
                trigger={<Button variant="secondary">{t("connectors.remove")}</Button>}
                title={t("connectors.remove.title", { name: connector.name })}
                description={t("connectors.remove.description", { name: connector.name })}
                confirmLabel={t("connectors.remove.confirm", { name: connector.name })}
                destructive
                onConfirm={async () => {
                  await workspace.removeConnector(connector.provider);
                  toast.neutral(t("connectors.removed", { name: connector.name }));
                  await navigate(listPath);
                }}
              />
            )
          ) : (
            <Button variant="primary" disabled={!connector.configured || !canManage} loading={busy} onClick={install}>
              {t("connectors.install")}
            </Button>
          )
        }
      />
      {!canManage && <Callout tone="info">{t("connectors.readOnly")}</Callout>}
      {detail && (
        <div className="dt-connector-detail">
          <Markdown>{detail}</Markdown>
        </div>
      )}
      <SettingsSection title={t("connectors.installation")}>
        {installation ? (
          <>
            <SettingsRow label={installation.externalTeamName || installation.externalTeamId} description={t("connectors.installedOn", { date: formatDate(installation.installedAt) })}>
              {installation.manageUrl && (
                <a className={buttonClass({ variant: "ghost", size: "sm" })} href={installation.manageUrl} target="_blank" rel="noopener noreferrer">
                  {t("connectors.configure", { name: connector.name })}
                </a>
              )}
            </SettingsRow>
            {installation.resources && (
              <SettingsBlock>
                <Resources resources={installation.resources} />
              </SettingsBlock>
            )}
          </>
        ) : (
          <SettingsBlock>
            <p className="dt-settings-row__desc">{!connector.configured ? t("connectors.notConfigured") : connector.pending ? t("connectors.pending", { name: connector.name }) : t(connector.provider === "notion" ? "connectors.notInstalled.notion" : "connectors.notInstalled")}</p>
          </SettingsBlock>
        )}
      </SettingsSection>
      {installation && connector.readsPages && <Sync connector={connector} workspace={workspace} canManage={canManage} />}
      {installation && (
        <SettingsSection title={t("connectors.links.title")}>
          {connector.canIdentify && (
            <SettingsRow label={t("connectors.identify.label", { name: connector.name })} description={mine ? t("connectors.identify.linkedAs", { name: mine.externalName }) : t("connectors.identify.unlinked")}>
              {!mine && (
                <Button variant="secondary" size="sm" loading={busy} onClick={identify}>
                  {t("connectors.identify", { name: connector.name })}
                </Button>
              )}
            </SettingsRow>
          )}
          {connector.links.length === 0 ? (
            <SettingsBlock>
              <p className="dt-settings-row__desc">{t(connector.canIdentify ? "connectors.links.emptyIdentify" : "connectors.links.empty")}</p>
            </SettingsBlock>
          ) : (
            connector.links.map((link) => <LinkRow key={link.externalUserId} link={link} provider={connector} workspace={workspace} canManage={canManage} onChanged={() => revalidator.revalidate()} />)
          )}
        </SettingsSection>
      )}
    </div>
  );
}

function BackToList({ to }: { to: string }) {
  const { t } = useI18n();
  return (
    <Link to={to} className="dt-connector-back">
      <ChevronLeftIcon size={12} />
      {t("connectors.title")}
    </Link>
  );
}

function Resolved({ connectors, provider, ...rest }: { connectors: ConnectorSummary[]; provider: string; workspace: WorkspaceApi; listPath: string; canManage: boolean; viewerId: string }) {
  const { t } = useI18n();
  useInstallOutcome(connectors);
  const connector = connectors.find((c) => c.provider === provider);
  if (!connector)
    return (
      <ErrorState
        title={t("error.notFound")}
        action={
          <LinkButton to={rest.listPath} variant="secondary" size="sm">
            {t("connectors.title")}
          </LinkButton>
        }
      />
    );
  return <Connector connector={connector} {...rest} />;
}

/** Back to the directory, the platform's header, what it does, its installation and who is linked. */
export function ConnectorSkeleton() {
  const { t } = useI18n();
  const { workspaceSlug } = useParams();
  return (
    <Loading>
      {/* One block, like the page itself, so the spacing is the page's. */}
      <div>
        <BackToList to={`/w/${workspaceSlug}/settings/connectors`} />
        <PageHeader
          title={
            <span className="dt-connector-title">
              <Skeleton shape="block" width={44} height={44} />
              <Skeleton width={140} />
            </span>
          }
          description={<Skeleton width={320} />}
          actions={<Skeleton shape="button" width={88} />}
        />
        <div className="dt-connector-detail">
          <Lines count={3} />
        </div>
        <SettingsSection title={t("connectors.installation")}>
          <SettingsRowsSkeleton count={1} />
        </SettingsSection>
        <SettingsSection title={t("connectors.links.title")}>
          <SettingsRowsSkeleton count={2} control={<Skeleton shape="badge" width={72} />} />
        </SettingsSection>
      </div>
    </Loading>
  );
}

export function ConnectorRoute() {
  const { workspace, workspaceSlug, provider, data } = useLoaderData<typeof connectorLoader>();
  return (
    <Deferred data={data} fallback={<ConnectorSkeleton />}>
      {({ connectors, canManage, viewerId }) => <Resolved connectors={connectors} provider={provider} workspace={workspace} listPath={`/w/${workspaceSlug}/settings/connectors`} canManage={canManage} viewerId={viewerId} />}
    </Deferred>
  );
}
